import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  TextField,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Refresh
} from '@mui/icons-material';

// File option interface for dropdowns
interface FileOption {
  label: string;
  value: string;
  path: string;
}

interface FileSelectionFormProps {
  stemWordFiles: FileOption[];
  keywordFiles: FileOption[];
  selectedStemWordFile: string;
  selectedKeywordFile: string;
  isLoadingFiles: boolean;
  customStemWordsPath: string;
  customKeywordsPath: string;
  error: string | null;
  isClassifying: boolean;
  onSelectedStemWordFileChange: (value: string) => void;
  onSelectedKeywordFileChange: (value: string) => void;
  onCustomStemWordsPathChange: (value: string) => void;
  onCustomKeywordsPathChange: (value: string) => void;
  onAddCustomStemWordsPath: () => void;
  onAddCustomKeywordsPath: () => void;
  onRefreshFiles: () => void;
  onStartClassification: () => void;
}

const FileSelectionForm: React.FC<FileSelectionFormProps> = ({
  stemWordFiles,
  keywordFiles,
  selectedStemWordFile,
  selectedKeywordFile,
  isLoadingFiles,
  customStemWordsPath,
  customKeywordsPath,
  error,
  isClassifying,
  onSelectedStemWordFileChange,
  onSelectedKeywordFileChange,
  onCustomStemWordsPathChange,
  onCustomKeywordsPathChange,
  onAddCustomStemWordsPath,
  onAddCustomKeywordsPath,
  onRefreshFiles,
  onStartClassification
}) => {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        Classification Configuration
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          File Selection
        </Typography>
        
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="stem-words-file-label">Stem Words File</InputLabel>
          <Select
            labelId="stem-words-file-label"
            value={selectedStemWordFile}
            label="Stem Words File"
            onChange={(e: SelectChangeEvent) => onSelectedStemWordFileChange(e.target.value)}
            disabled={isLoadingFiles || stemWordFiles.length === 0}
          >
            {stemWordFiles.map((file) => (
              <MenuItem key={file.value} value={file.path}>
                {file.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedStemWordFile ? 
              (selectedStemWordFile.endsWith('.json') ? 
                "Using JSON file with topic hierarchy from Module 1." : 
                "Using CSV file for stem words (legacy format)")
              : "No stem word file selected"}
          </Typography>
        </FormControl>
        
        {/* Direct path input for Stem Words File */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Or enter file path directly:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs>
              <TextField
                size="small"
                fullWidth
                label="Direct Path to Stem Words File"
                placeholder="e.g., client-data/clientId/workflows/workflowId/outputs/module1/final_stemwords.json"
                value={customStemWordsPath}
                onChange={(e) => onCustomStemWordsPathChange(e.target.value)}
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="medium"
                onClick={onAddCustomStemWordsPath}
                disabled={!customStemWordsPath}
              >
                Use Path
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="keywords-file-label">Keywords File</InputLabel>
          <Select
            labelId="keywords-file-label"
            value={selectedKeywordFile}
            label="Keywords File"
            onChange={(e: SelectChangeEvent) => onSelectedKeywordFileChange(e.target.value)}
            disabled={isLoadingFiles || keywordFiles.length === 0}
          >
            {keywordFiles.map((file) => (
              <MenuItem key={file.value} value={file.path}>
                {file.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Direct path input for Keywords File */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Or enter keywords file path directly:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs>
              <TextField
                size="small"
                fullWidth
                label="Direct Path to Keywords File"
                placeholder="e.g., client-data/clientId/workflows/workflowId/input/keywords.csv"
                value={customKeywordsPath}
                onChange={(e) => onCustomKeywordsPathChange(e.target.value)}
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="medium"
                onClick={onAddCustomKeywordsPath}
                disabled={!customKeywordsPath}
              >
                Use Path
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={onRefreshFiles}
            disabled={isLoadingFiles}
          >
            {isLoadingFiles ? 'Loading...' : 'Refresh Files'}
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Button
        variant="contained"
        color="primary"
        disabled={
          isClassifying || 
          !selectedStemWordFile || 
          !selectedKeywordFile || 
          stemWordFiles.length === 0 || 
          keywordFiles.length === 0
        }
        fullWidth
        startIcon={isClassifying ? <CircularProgress size={20} /> : <AssessmentIcon />}
        sx={{ mt: 2 }}
        onClick={onStartClassification}
      >
        {isClassifying ? 'Classifying Keywords...' : 'Start Classification'}
      </Button>
    </>
  );
};

export default FileSelectionForm;
